/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, Checkbox, pitchTypeOrSubType, pitchSubTypeColor } from 'yankees-react-utils';
import { Row, Col } from 'react-bootstrap';
import { useOktaQueries, PlayerReport, Stats, HeatMaps, StrikeZones, MLBAMLiveFeed, usePitchVideoFn, useOktaQuery } from '../../../data';
import { mapPPFXReport } from '../../../data/dataMapping';
import StateTabs from '../../../components/StateTabs';
import ReportCard, { StyledReportCard, reportCardBorder } from '../../../components/ReportCard';
import PitchBreakPlotChart from '../../../components/PitchBreakPlotChart';
import GameStats from './GameStats';
import PitchUsage from './PitchUsage';
import PitchTypeCharts from '../../Reports/PitcherStrengths/PPFX/PitchTypeCharts';
import PitchLocationCharts from '../../Reports/PitcherStrengths/PPFX/PitchLocationCharts';
import usePlayerSelector from '../../../utils/usePlayerSelector';
import usePlayerSettings from '../../../utils/usePlayerSettings';
import {
    buildPlaylist,
    mapLiveDataToPpfxPitches,
    mapLiveDataToPpfxReport,
} from '../Live/mlbamMapping';

const StyledPPFXPitchTabs = styled.div`
    > div + div {
        margin: 0;
        border: ${reportCardBorder};
        border-radius: 0.25rem;
        border-radius-top-left: 0;
    }
`;
const PPFXToggleWrapper = styled.div`
    text-align: right;
    &&& {
        margin-bottom: 0;
    }
`;
const Container = styled.div`
    margin: 20px auto;
    > * {
        margin-bottom: 20px;
    }
    ${StyledReportCard} .card-body > h4, ${StyledPPFXPitchTabs} > div + div > h4 {
        font-size: 18px;
        font-weight: bold;
        text-align: left;
        text-transform: uppercase;
    }
    .table {
        font-size: 12px;
    }
`;
const StyledPitchUsageContainer = styled.div`
    h4 {
        font-size: 18px;
        font-weight: bold;
        text-align: center;
    }
`;

const buildQueries = (player, gameid, game) => {
    const playerId = player.PlayerID;
    const { yearid, levelid } = game;
    const minyear = yearid - 1;
    const maxyear = yearid;
    const islhp = player.Throws === 2;
    const cacheOpts = { staleTime: 1000 * 60 * 30, cacheTime: 1000 * 60 * 30, gcTime: 1000 * 60 * 30 };

    const queries = [
        { queryKey: ['ppfx', playerId, gameid], queryFn: (t) => PlayerReport.ppfx(t, { playerId, gameid, usesubtypes: true }) },
        { queryKey: ['ppfxAvgs', 8, minyear, maxyear, islhp], queryFn: (t) => PlayerReport.ppfxavgs(t, { levelid: 8, minyear, maxyear, islhp }), ...cacheOpts },
        { queryKey: ['lgaStats', islhp], queryFn: (t) => Stats.pitchtypebreakdownpitchfxaverages(t, { islhp, islhb: '' }), ...cacheOpts },
        { queryKey: ['heatMapsPts', playerId], queryFn: (t) => HeatMaps.getPitcherHeatMaps(t, playerId), retry: false },
        { queryKey: ['heatMapsLocations', playerId, yearid], queryFn: (t) => HeatMaps.pitchLocationActual(t, playerId, { yearid }), retry: false },
        { queryKey: ['pitchbreaks', playerId, yearid], queryFn: (t) => Stats.pitchbreaks(t, { playerid: playerId, yearid }) },
        { queryKey: ['strikeZoneContours', true, yearid, levelid], queryFn: (t) => StrikeZones.getStrikeZoneContours(t, { islhb: true, yearid, levelid }), ...cacheOpts },
        { queryKey: ['strikeZoneContours', false, yearid, levelid], queryFn: (t) => StrikeZones.getStrikeZoneContours(t, { islhb: false, yearid, levelid }), ...cacheOpts },
        { queryKey: ['pitchUsageVsLhh', playerId], queryFn: (t) => PlayerReport.pts(t, playerId, { vslh: true }) },
        { queryKey: ['pitchUsageVsRhh', playerId], queryFn: (t) => PlayerReport.pts(t, playerId, { vslh: false }) },
    ];
    return queries;
};

const mapDataQueriesResponse = (translate, dataQueries, player, game, isLive, statsApiFeed, livePitches, classifiedPitches) => {
    const { gameid } = game;
    const ppfxParams = { playerId: player.PlayerID, gameid };
    let ppfx = dataQueries[0]?.data || {};
    if (isLive) {
        ppfx = mapLiveDataToPpfxReport(game, player.PlayerID, ppfx, statsApiFeed, livePitches, classifiedPitches);
    }
    ppfx = mapPPFXReport(translate, ppfxParams, ppfx);
    const ppfxAvgs = dataQueries[1]?.data || {};
    const lgastats = dataQueries[2]?.data || [];
    const heatMapsPts = dataQueries[3]?.data || [];
    const heatMapsLocations = dataQueries[4]?.data || [];
    const breaks = dataQueries[5]?.data || [];
    // convert PascalCased keys to lowercase
    const seasonPitches = breaks?.pitchbreaks?.map((p) => ({
        pitchtype: p.fxtype,
        ...p,
    })) || [];
    const strikeZoneContoursLhb = dataQueries[6]?.data || {};
    const strikeZoneContoursRhb = dataQueries[7]?.data || {};
    const strikeZoneContours = {
        lhb: [],
        rhb: [],
    };
    const lhbSZContours = strikeZoneContoursLhb;
    const rhbSZContours = strikeZoneContoursRhb;
    strikeZoneContours.lhb = player.Throws === 1 ? lhbSZContours.rhp : lhbSZContours.lhp;
    strikeZoneContours.rhb = player.Throws === 1 ? rhbSZContours.rhp : rhbSZContours.lhp;
    const pitchUsageLhh = dataQueries[8]?.data || {};
    const pitchUsageRhh = dataQueries[9]?.data || {};

    return {
        lgastats,
        ppfx,
        ppfxAvgs,
        heatMapsPts,
        heatMapsLocations,
        strikeZoneContours,
        teams: ppfx.teams,
        playerStrikes: ppfx?.playerstrikes || [],
        totalStrikes: ppfx?.totalstrikes || [],
        pitchUsageLhh,
        pitchUsageRhh,
        seasonPitches,
    };
};

const PitchFX = ({ playerid, gameid, game, isGameLoading, isInBats, isLive }) => {
    const { t: translate } = useTranslation();
    // Load Player
    const [showMinLgStats, setShowMinLgStats] = useState(false);
    const player = usePlayerSelector();
    const pitchVideoFn = usePitchVideoFn();
    const { ShowGameScore } = usePlayerSettings();

    useEffect(() => { if (game && game.isminorleague) setShowMinLgStats(true); }, [game]);

    let dataQueries = [];
    if (!isGameLoading) {
        dataQueries = buildQueries(player, gameid, game);
    }

    const dataQueriesResponse = useOktaQueries(dataQueries);

    // load live data if necessary
    const { data: statsApiFeed, isLoading: loadingStatsApi } = useOktaQuery(
        ['statsapi', game.mlbamgamepk],
        (t) => MLBAMLiveFeed.gameFeed(t, game.mlbamgamepk, { hydrate: 'alignment,preState,strikeZone' }),
        { enabled: !!game.mlbamgamepk && isLive },
    );
    const { data: statcastGuids, isLoading: loadingStatcastGuids } = useOktaQuery(
        ['statcast', game.mlbamgamepk],
        (t) => MLBAMLiveFeed.statcastPlays(t, game.mlbamgamepk, { hydrate: 'analytics(video,pitch,hit,metrics,strikeZone,metaData)' }),
        { enabled: !!game.mlbamgamepk && isLive },
    );
    // shortcut: map ABs situation onto each pitch of AB
    // eslint-disable-next-line no-unused-expressions
    statsApiFeed?.liveData.plays?.allPlays?.forEach((play) => {
        play.playEvents.forEach((pitch) => {
            pitch.about = { ...play.about };
            pitch.matchup = { ...play.matchup };
        });
    });
    // map pitches from live data
    let livePitches = [];
    if (isLive && statsApiFeed && statcastGuids) {
        livePitches = mapLiveDataToPpfxPitches(game, player.PlayerID, statsApiFeed, statcastGuids);
    }
    // classify live pitches
    const { data: classifiedPitches, isLoading: isClassifying } = useOktaQuery(
        ['classifier', playerid, gameid, livePitches[0]],
        (t) => MLBAMLiveFeed.classifyPitches(t, livePitches.map((p) => ({
            pitcherid: p.pitcherid, // the order of these fields seems to be required to match the order of the db type
            yearid: game.yearid,
            playid: p.playid,
            islhp: p.islhp,
            islhb: p.islhb,
            initspeed: p.initspeed54,
            breakx: p.breakx,
            breakz: p.breakz,
            spinrate: p.spinrate,
            initposx: p.initposx,
            initposz: p.initposz,
        }))),
        { retry: 0, enabled: isLive && livePitches?.length > 0 },
    );

    if (isGameLoading || loadingStatsApi || loadingStatcastGuids || isClassifying || dataQueriesResponse.some((q) => q.isLoading)) {
        return <div style={{ margin: '10px' }}><LoadingSpinner /></div>;
    }

    if (isLive && !statsApiFeed?.liveData?.plays?.allPlays?.some((p) => p.matchup?.pitcher.id === player.PlayerID)) {
        return <h3 className="mt-5 mb-5">{translate('sections.post-game.home.no-data-built')}</h3>;
    }

    const data = mapDataQueriesResponse(translate, dataQueriesResponse, player, game, isLive, statsApiFeed, livePitches, classifiedPitches);
    const { ppfx, ppfxAvgs, lgastats, heatMapsPts, heatMapsLocations, strikeZoneContours, pitchUsageLhh, pitchUsageRhh } = data;

    const islhp = player.Throws === 2;

    const initpos_minX = ppfx && Math.min(...ppfx?.pitches.map((d) => d.initposx));
    const initpos_maxX = ppfx && Math.max(...ppfx?.pitches.map((d) => d.initposx));
    const initpos_minZ = ppfx && Math.min(...ppfx?.pitches.map((d) => d.initposz));
    const initpos_maxZ = ppfx && Math.max(...ppfx?.pitches.map((d) => d.initposz));
    const bufferX = (initpos_maxX - initpos_minX) * 0.1; // 10% is our coverage
    const bufferZ = (initpos_maxZ - initpos_minZ) * 0.1;
    const initPlot_minX = initpos_minX - bufferX;
    const initPlot_minY = initpos_minZ - bufferZ;
    const initPlot_maxX = initpos_maxX + bufferX;
    const initPlot_maxY = initpos_maxZ + bufferZ;

    const livePitchVideoFn = (gmid, eventseq, pitchseq, playid) => {
        const pitches = (statsApiFeed?.liveData?.plays?.allPlays ?? [])
            .flatMap((play) => play.playEvents)
            .filter((pitch) => pitch.playId === playid);
        return buildPlaylist(pitches, statcastGuids, translate);
    };
    const livePlaylistFn = ({ pitcherid, islhb, fxpitchtype, fxsubtype }) => {
        const selectedPitches = ppfx?.pitches?.filter((p) => p.pitcherid === pitcherid
            && (islhb === null || islhb === undefined || p.islhb === islhb)
            && (fxpitchtype === null || fxpitchtype === undefined || p.pitchtype === fxpitchtype)
            && (fxsubtype === null || fxsubtype === undefined || p.fxsubtype === fxsubtype));
        const apiPlays = (statsApiFeed?.liveData?.plays?.allPlays ?? [])
            .flatMap((play) => play.playEvents)
            .filter((apiPitch) => (selectedPitches ?? []).some((p) => p.playid === apiPitch.playId));
        return buildPlaylist(apiPlays, statcastGuids, translate);
    };

    const pitchStatTabs = ppfx.pitchtypes.filter((type) => type.pitchtype !== 1).map((type) => {
        const gameStatsForPitchType = ppfx.gamestats.find((g) => g.pitchtype === type.pitchtype);
        const key = type.fxsubtype || type.pitchtype || 0;
        return gameStatsForPitchType && gameStatsForPitchType.totalpitches > 0 ? {
            key,
            title: pitchTypeOrSubType(key, undefined, undefined, translate),
            component: (
                <>
                    <h4>{`${pitchTypeOrSubType(type.fxsubtype || type.pitchtype || 0, undefined, undefined, translate)} ${translate('sections.post-game.ppfx.stats')}`}</h4>
                    <StyledPitchUsageContainer>
                        <h4>{translate('sections.post-game.ppfx.pitch-usage')}</h4>
                        <PitchUsage gameId={gameid} yearId={game.yearid} pitchType={type} playerId={playerid} ppfxData={ppfx} pitchUsageLhh={pitchUsageLhh} pitchUsageRhh={pitchUsageRhh} />
                    </StyledPitchUsageContainer>
                    <PitchTypeCharts
                        playerid={playerid}
                        type={type}
                        pitchtypeseries={ppfx.pitchtypeseries}
                        gamestats={ppfx.gamestats.filter(({ pitchtype, fxsubtype, filteredlevelid }) => type.pitchtype === pitchtype && type.fxsubtype === fxsubtype && filteredlevelid === null)}
                        pitchTypeSplits={ppfx.splits}
                        seasonstats={ppfx.seasonstats.filter(({ pitchtype, fxsubtype }) => type.pitchtype === pitchtype && type.fxsubtype === fxsubtype)}
                        lgastats={lgastats.filter(({ pitchtype }) => type.pitchtype === pitchtype)}
                        islhp={islhp}
                        heatMapsPts={heatMapsPts.filter((h) => h.PitchType === type.pitchtype && (!type.fxsubtype || h.FxSubType === type.fxsubtype))}
                        heatMapsLocations={heatMapsLocations.filter((h) => h.PitchType === type.pitchtype && (!type.fxsubtype || h.FxSubType === type.fxsubtype))}
                        strikeZoneContours={strikeZoneContours}
                        pitchtypestdev={ppfxAvgs.pitchtypestdev}
                        dataPoints={gameStatsForPitchType?.totalpitches}
                        dataPointType="PitchNumber"
                        isInBats={isInBats}
                        isLive={isLive}
                        pitchVideoFn={isLive ? livePitchVideoFn : (isInBats ? pitchVideoFn : null)}
                        livePlaylistFn={isLive ? livePlaylistFn : null}
                        useSubType
                        showGameFxEra={ShowGameScore}
                        boldSummary
                        showExecution={!isLive}
                    />
                </>
            ),
        } : null;
    }).filter((x) => x);

    return (
        <Container>
            <PPFXToggleWrapper>
                <Checkbox
                    id="showMinLgStats"
                    label={translate('sections.post-game.ppfx.show-minor-stats')}
                    checked={showMinLgStats}
                    onChange={(e) => setShowMinLgStats(e.currentTarget.checked)}
                />
            </PPFXToggleWrapper>
            <ReportCard title={translate('sections.post-game.ppfx.game-stats')}>
                <GameStats
                    data={ppfx}
                    ppfxAvgs={ppfxAvgs.seasonstdev}
                    levelId={game.levelid}
                    isLhp={islhp}
                    showMinLgStats={showMinLgStats}
                    isLive={isLive}
                    livePlaylistFn={livePlaylistFn}
                />
            </ReportCard>
            <StyledPPFXPitchTabs>
                {pitchStatTabs.length ? <StateTabs tabs={pitchStatTabs} /> : (
                    <div className="d-flex justify-content-center py-5">
                        {translate('sections.post-game.ppfx.no-data')}
                    </div>
                )}
            </StyledPPFXPitchTabs>
            <Row>
                <Col md={6}>
                    <ReportCard title={translate('sections.post-game.ppfx.pitch-break-plot')}>
                        <PitchBreakPlotChart
                            enableZoom={false}
                            pitches={ppfx.pitches}
                            allPitches={data.seasonPitches}
                            width={300}
                            height={300}
                            keyX="breakx54"
                            keyY="breakz54"
                            pitchVideoFn={isLive ? livePitchVideoFn : (isInBats ? pitchVideoFn : null)}
                            colorFunc={(pt) => pitchSubTypeColor(pt)}
                        />
                    </ReportCard>
                </Col>
                <Col md={6}>
                    <ReportCard title={translate('sections.post-game.ppfx.release-location')}>
                        <PitchBreakPlotChart
                            enableZoom={false}
                            pitches={ppfx.pitches}
                            width={300}
                            height={300}
                            keyX="initposx"
                            keyY="initposz"
                            labelX="Release X"
                            labelY="Release Z"
                            minX={initPlot_minX}
                            minY={initPlot_minY}
                            maxX={initPlot_maxX}
                            maxY={initPlot_maxY}
                            pitchVideoFn={isLive ? livePitchVideoFn : (isInBats ? pitchVideoFn : null)}
                            colorFunc={(pt) => pitchSubTypeColor(pt)}
                        />
                    </ReportCard>
                </Col>
            </Row>
            <ReportCard title={translate('sections.post-game.ppfx.pitch-location-count')}>
                <PitchLocationCharts
                    countseries={ppfx.countseries}
                    strikeZoneContours={strikeZoneContours}
                    isInBats={isInBats || isLive}
                    pitchVideoFn={isLive ? livePitchVideoFn : (isInBats ? pitchVideoFn : null)}
                    useABSRulebookZone
                    useBaseballMarkers
                />
            </ReportCard>
        </Container>
    );
};

PitchFX.propTypes = {
    playerid: PropTypes.number.isRequired,
    gameid: PropTypes.number.isRequired,
    isGameLoading: PropTypes.bool.isRequired,
    game: PropTypes.shape({
        yearid: PropTypes.number.isRequired,
        levelid: PropTypes.number.isRequired,
        isminorleague: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]).isRequired,
        hpname: PropTypes.string,
        mlbamgamepk: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    isInBats: PropTypes.bool,
    isLive: PropTypes.bool,
};
PitchFX.defaultProps = {
    game: undefined,
    isInBats: false,
    isLive: false,
};

export default PitchFX;
